import { type ReactNode } from 'react';
import FormattedText from '@nespresso/catalog-components-react/containers/FormattedText';
import ThumbnailNav from '@nespresso/catalog-components-react/containers/ThumbnailNav';
import { Notification } from '@nespresso-design-system/components';
import styles from './ModelOption.module.scss';
import type { Product, Range } from '@/domain/Product';
import useTranslations from '@/widgets/hooks/useTranslations.tsx';
import { hasPromotions } from '@/domain/Product.ts';
import Header from './parts/Header/Header.tsx';
import type { Translations } from '../../../config/index.ts';
import { trackRanges } from '../../../Tracking.ts';
import PromotionAction from './parts/PromotionAction/PromotionAction.tsx';

export type ModelOptionProps = {
  title?: string;
  product: Product;
  range: Range;
  fromPrice?: number;
  offerText?: string;
};

export default function ModelOption({
  title,
  product,
  range,
  fromPrice,
  offerText
}: ModelOptionProps) {
  if (!range || range.products.length === 0) return null;
  const { t, interpolate } = useTranslations<Translations>();
  const subscriptionOnlyLabel = t(
    'productDetailsHero.productVariantsSection.onlyAvailableWithSubscription'
  );
  const isCurrentRange = isProductInRange(product, range);
  const firstBuyableProduct = getFirstBuyableProduct(range.products);
  const firstProductUrl = `${range.products.at(0)!.slug}#choose-model`;

  const hasOffersText = !!offerText;
  const offerTextInterpolated = interpolate(offerText ?? '', {
    price: fromPrice
  });

  return isCurrentRange ? (
    <div class={styles.card}>
      <Header
        title={title}
        product={product}
        offerText={offerTextInterpolated}
      />

      <p class={styles.colorLabel}>
        <span class={styles.chooseLabel}>
          {t('productDetailsHero.productVariantsSection.chooseColourLabel')}
        </span>{' '}
        <span class={styles.productColor}>{product.color?.name}</span>
      </p>
      <ThumbnailNav aria-label={t('productDetailsHero.chooseYoutModel')}>
        {range.products
          .filter((prod) => prod.color)
          .map((prod) => (
            <ThumbnailNav.Thumbnail
              title={prod.color?.name!}
              url={`${prod.slug}#choose-model`}
              imageUrl={`${prod.mainImage.url}?impolicy=small&imwidth=50`}
              imageAltText={prod.mainImage.alt}
              isCurrentPage={prod.id === product.id}
              hasDot={hasOffersText && (hasPromotions(prod) || prod.inStock)}
              className={styles.thumbnail}
            />
          ))}
      </ThumbnailNav>

      {product.isSubscriptionOnly && (
        <Notification hasDismissButton={false}>
          {
            (
              <FormattedText>{subscriptionOnlyLabel}</FormattedText>
            ) as ReactNode
          }
        </Notification>
      )}

      <PromotionAction product={product} />
    </div>
  ) : (
    // Show range card as clickable & collapsed
    <a
      class={styles.card}
      href={firstProductUrl}
      aria-label={title}
      onClick={() => trackRanges(title || '')}
    >
      <Header
        title={title}
        // will show price for first buyable product without subscription, when panel is collapsed
        product={firstBuyableProduct}
        offerText={offerTextInterpolated}
      />
    </a>
  );
}

function isProductInRange(product: Product, range: Range): boolean {
  return range.products.find((prod) => prod.id === product.id) !== undefined;
}

/** Return first product which is not subscription only, if exists */
function getFirstBuyableProduct(products: Product[]): Product | undefined {
  return products.find((prod) => !prod.isSubscriptionOnly);
}
